'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { List, X } from '@phosphor-icons/react'
import { FlowLogo } from './FlowLogo'

interface FlowNavItem {
  label: string
  href: string
  external?: boolean
}

interface FlowNavBarProps {
  items?: FlowNavItem[]
  logoHref?: string
  ctaLabel?: string
  ctaHref?: string
  diagnosticLabel?: string
  diagnosticHref?: string
}

interface FlowProduct {
  name: string
  description: string
  href: string
  illustration: string
  illustrationHover: string
  illustrationStyle?: React.CSSProperties
}

const FLOW_PRODUCTS: FlowProduct[] = [
  {
    name: 'Flow Auth',
    description: 'Flow Auth automates prior authorizations across modalities by detecting PA requirements, assembling packets, submitting and tracking every request in one connected system.',
    href: 'https://go-flow.ai/prior-authorization',
    illustration: '/media/product-icons/flow-auth-default.png',
    illustrationHover: '/media/product-icons/flow-auth-hover.png',
  },
  {
    name: 'Flow Capture',
    description: 'An AI-powered clinical documentation and autonomous coding solution that captures conversations at the point of care and generates complete, compliant codes for clean claims.',
    href: 'https://go-flow.ai/flow-capture',
    illustration: '/media/product-icons/flow-capture-default.png',
    illustrationHover: '/media/product-icons/flow-capture-hover.png',
  },
  {
    name: 'Flow Collect',
    description: 'AI-powered denial management and revenue recovery that works the full revenue cycle.',
    href: 'https://go-flow.ai/flow-collect',
    illustration: '/media/product-icons/flow-collect-default.png',
    illustrationHover: '/media/product-icons/flow-collect-hover.png',
  },
]

const DEFAULT_ITEMS: FlowNavItem[] = [
  { label: 'HOME',       href: 'https://go-flow.ai',           external: true },
  { label: 'PRODUCTS',   href: 'https://go-flow.ai/#products', external: true },
  { label: 'STORIES',    href: '/flow/blogs',                  external: false },
  { label: 'RESOURCES',  href: '/flow/resources',              external: false },
  { label: 'INNOVACCER', href: 'https://innovaccer.com',       external: true },
]

function NavLink({ item, onClick }: { item: FlowNavItem; onClick?: () => void }) {
  if (item.external) {
    return <a href={item.href} className="flow-nav__item" onClick={onClick}>{item.label}</a>
  }
  return <Link href={item.href} className="flow-nav__item" onClick={onClick}>{item.label}</Link>
}

function ProductsDropdown({ onClose }: { onClose: () => void }) {
  return (
    <div className="flow-products-dropdown__inner">
      {FLOW_PRODUCTS.map((p) => (
        <a key={p.name} href={p.href} className="flow-product-card" onClick={onClose}>
          {/* Header — card itself has p-[24px] matching Figma */}
          <div className="flow-product-card__header">
            <div className="flow-product-card__title-row">
              <span className="flow-product-card__name">{p.name}</span>
              <span className="flow-product-card__arrow" aria-hidden="true">▶</span>
            </div>
            <p className="flow-product-card__desc">{p.description}</p>
          </div>
          {/* Illustration — bg #151515, h 192px, radius 4px, exact Figma.
              Two stacked variants (default + hover) crossfade on card hover. */}
          <div className="flow-product-card__illustration">
            <img
              className="flow-product-card__illus-default"
              src={p.illustration}
              alt=""
              aria-hidden="true"
              style={p.illustrationStyle}
            />
            <img
              className="flow-product-card__illus-hover"
              src={p.illustrationHover}
              alt=""
              aria-hidden="true"
              style={p.illustrationStyle}
            />
          </div>
        </a>
      ))}
    </div>
  )
}

export function FlowNavBar({
  items = DEFAULT_ITEMS,
  logoHref = 'https://go-flow.ai',
  ctaLabel = 'REQUEST A DEMO',
  ctaHref = 'https://go-flow.ai/request-a-demo',
  diagnosticLabel = 'GET FREE DIAGNOSTIC',
  diagnosticHref = 'https://go-flow.ai/ai-driven-revenue-cycle-intelligence',
}: FlowNavBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [mobileProdOpen, setMobileProdOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [navBottom, setNavBottom] = useState(0)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => { setMounted(true) }, [])

  const openProducts = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    // Capture nav bottom edge for the portal backdrop (same pattern as NavBarMain)
    if (headerRef.current) {
      setNavBottom(Math.round(headerRef.current.getBoundingClientRect().bottom))
    }
    setProductsOpen(true)
  }, [])

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setProductsOpen(false), 250)
  }, [])

  return (
    <>
    <header className="flow-nav" ref={headerRef}>
      <div className="flow-nav__card">
        <div className="flow-nav__left">
          <a href={logoHref} className="flow-nav__logo" aria-label="flow by innovaccer">
            <FlowLogo height={40} />
          </a>
          <nav className="flow-nav__menu" aria-label="Primary">
            {items.map((item) => {
              if (item.label === 'PRODUCTS') {
                return (
                  <div key="PRODUCTS" className="flow-nav__products-trigger"
                    onMouseEnter={openProducts}
                    onMouseLeave={scheduleClose}>
                    <button type="button" className="flow-nav__item flow-nav__item--btn"
                      aria-haspopup="true" aria-expanded={productsOpen}>
                      {item.label}
                    </button>
                  </div>
                )
              }
              return <NavLink key={item.label} item={item} />
            })}
          </nav>
        </div>

        <div className="flow-nav__right">
          <a href={diagnosticHref} className="flow-nav__cta flow-nav__cta--outline">
            <span className="flow-nav__cta-text">
              <span className="btn-primary-text">{diagnosticLabel}</span>
              <span className="btn-hover-text">{diagnosticLabel}</span>
            </span>
          </a>
          <a href={ctaHref} className="flow-nav__cta flow-nav__cta--solid">
            <span className="flow-nav__cta-text">
              <span className="btn-primary-text">{ctaLabel}</span>
              <span className="btn-hover-text">{ctaLabel}</span>
            </span>
          </a>
          <button
            type="button"
            className="flow-nav__hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Backdrop portalled to body — starts at nav bottom edge, never covers the nav itself */}

      {/* Desktop products dropdown — hover-activated, renders outside the card */}
      {productsOpen && (
        <div className="flow-products-dropdown"
          role="dialog"
          aria-label="Products"
          onMouseEnter={openProducts}
          onMouseLeave={scheduleClose}>
          <ProductsDropdown onClose={() => setProductsOpen(false)} />
        </div>
      )}

      {/* Mobile drawer */}
      <div className={`flow-nav__mobile${mobileOpen ? ' is-open' : ''}`}>
        <nav className="flow-nav__mobile-menu" aria-label="Mobile primary">
          {items.map((item) => {
            if (item.label === 'PRODUCTS') {
              return (
                <div key="PRODUCTS" className="flow-nav__mobile-products">
                  <button
                    type="button"
                    className="flow-nav__mobile-products-btn"
                    onClick={() => setMobileProdOpen((o) => !o)}
                    aria-expanded={mobileProdOpen}
                  >
                    <span className="flow-nav__mobile-nav-item__label">PRODUCTS</span>
                    <span className={`flow-nav__mobile-products-chevron${mobileProdOpen ? ' is-open' : ''}`} aria-hidden="true">▶</span>
                  </button>
                  {mobileProdOpen && (
                    <div className="flow-nav__mobile-products-list">
                      {FLOW_PRODUCTS.map((p) => (
                        <a
                          key={p.name}
                          href={p.href}
                          className="flow-nav__mobile-product-card"
                          onClick={() => { setMobileOpen(false); setMobileProdOpen(false) }}
                        >
                          <div className="flow-nav__mobile-product-card__header">
                            <span className="flow-nav__mobile-product-card__name">{p.name}</span>
                            <span className="flow-nav__mobile-product-card__arrow" aria-hidden="true">▶</span>
                          </div>
                          <p className="flow-nav__mobile-product-card__desc">{p.description}</p>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )
            }
            return item.external
              ? <a key={item.label} href={item.href} className="flow-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="flow-nav__mobile-nav-item__label">{item.label}</span>
                </a>
              : <Link key={item.label} href={item.href} className="flow-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="flow-nav__mobile-nav-item__label">{item.label}</span>
                </Link>
          })}
        </nav>

        <div className="flow-nav__mobile-ctas">
          <a
            href={ctaHref}
            className="flow-nav__cta flow-nav__cta--solid flow-nav__cta--block"
            onClick={() => setMobileOpen(false)}
          >
            <span className="flow-nav__cta-text">
              <span className="btn-primary-text">{ctaLabel}</span>
              <span className="btn-hover-text">{ctaLabel}</span>
            </span>
          </a>
        </div>
      </div>
    </header>

    {/* Blur backdrop — portalled to document.body so it starts exactly at nav bottom
        and never overlaps the nav itself (same pattern as NavBarMain) */}
    {mounted && productsOpen && createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          top: navBottom,
          zIndex: 999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          background: 'rgba(0, 0, 0, 0.15)',
          pointerEvents: 'auto',
        }}
        onClick={() => setProductsOpen(false)}
        aria-hidden="true"
      />,
      document.body
    )}
  </>
  )
}

export default FlowNavBar
